import {useAutomationViewPage} from './AutomationView.hooks';
import {AutomationReviewRequestsTable} from './AutomationReviewRequestsTable.tsx';
import ArrowBackIcon from '@app/assets/icons/back.svg?react';
import eyeIcon from '@app/assets/icons/eye.png';
import EmailIcon from '@app/assets/icons/mail-box.svg?react';
import IPhoneIcon from '@app/assets/icons/iphone.svg?react';
import CalendarIcon from '@app/assets/icons/calendar-01.svg?react';
import RemoveIcon from '@app/assets/icons/remove-circle.svg?react';
import AutomationIcon from '@app/assets/icons/automation-button.svg?react';
import CustomerMultipleIcon from '@app/assets/icons/customer-multiple.svg?react';
import FolderIcon from '@app/assets/icons/folder.svg?react';
import BookIcon from '@app/assets/icons/book-03.svg?react';
import FullStarIcon from '@app/assets/icons/star-full.svg?react';


import {capitalize, startCase} from "lodash";
import {getAutomationStatusColor} from "@app/utils/utils.tsx";
import {AUTOMATION_EVENT_NAME_VALUE, AUTOMATION_EVENT_SOURCE_VALUE, AutomationEventNameType, AutomationEventSourceType} from "@app/models/automations.ts";
import {ListInterface, TagInterface} from "@app/models";

export const AutomationView = () => {
  const { navigate, automationData, isLoading } = useAutomationViewPage();

  const automation = automationData?.data;
  const followUps = automation?.followUps || [];

  const isReviewEvent = [AUTOMATION_EVENT_NAME_VALUE.REVIEW_ADDED, AUTOMATION_EVENT_NAME_VALUE.REVIEW_UPDATED].includes(automation?.eventName);

  // Parse channels as array (comma-separated string)
  const channels = (automation?.channels || '').split(',').map((p: string) => p.trim().toLowerCase());
  const hasEmail = channels.includes('email');
  const hasSMS = channels.includes('sms');
  const hasReviewRequests = [AUTOMATION_EVENT_NAME_VALUE.CUSTOMER_ADDED, AUTOMATION_EVENT_NAME_VALUE.CUSTOMER_DELETED].includes(automation?.eventName);
  const hasFollowUps = [AUTOMATION_EVENT_NAME_VALUE.CUSTOMER_ADDED, AUTOMATION_EVENT_NAME_VALUE.CUSTOMER_DELETED].includes(automation?.eventName);
  const hasCustomerFilter =
    [AUTOMATION_EVENT_NAME_VALUE.CUSTOMER_ADDED, AUTOMATION_EVENT_NAME_VALUE.CUSTOMER_DELETED].includes(automation?.eventName)
    &&
    [AUTOMATION_EVENT_SOURCE_VALUE.SYSTEM].includes(automation?.eventSource)
  ;

  const getEventText = (event: AutomationEventNameType) => {
    switch (event) {
      case AUTOMATION_EVENT_NAME_VALUE.CUSTOMER_ADDED:
        return 'Customer is added';
      case AUTOMATION_EVENT_NAME_VALUE.CUSTOMER_DELETED:
        return 'Customer is deleted';
      case AUTOMATION_EVENT_NAME_VALUE.REVIEW_ADDED:
        return 'Review is added';
      case AUTOMATION_EVENT_NAME_VALUE.REVIEW_UPDATED:
        return 'Review is updated';
      default:
        return capitalize((event as string).replace('_', ' '));

    }
  };

  const getEventSourceText = (source: AutomationEventSourceType) => {
    switch (source) {
      case AUTOMATION_EVENT_SOURCE_VALUE.GOOGLE_REVIEWS:
        return 'Google reviews';
      case AUTOMATION_EVENT_SOURCE_VALUE.SYSTEM:
        return 'Echo Ratings';
      case AUTOMATION_EVENT_SOURCE_VALUE.SQUAREUP:
        return 'Square Up';
      case AUTOMATION_EVENT_SOURCE_VALUE.JOBBER:
        return 'Jobber';
      default:
        return capitalize((source as string).replace('_', ' '));

    }
  };

  if (isLoading) {
    return <div className="container mx-auto p-4">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-base-seasalt">
      {/* Header */}
      <div className="bg-white border-b border-b-base-ghostWhite ">
        <div className="container mx-auto p-4 flex gap-3 justify-start items-center">
          <h2 className="font-bold text-[24px] flex items-center">
            <button onClick={() => navigate('/automations')} className="mr-2">
              <ArrowBackIcon/>
            </button>
            {automation?.name || 'Automation name'}
          </h2>
          {((hasEmail) && <span className="bg-base-antiFlash p-2 rounded-sm flex items-center justify-center">
            <EmailIcon className="w-5 h-5" />
          </span>)}
          {((hasSMS) && <span className="bg-base-antiFlash p-2 rounded-sm flex items-center justify-center">
            <IPhoneIcon className="w-5 h-5" />
          </span>)}
          <span className="flex items-center gap-2 ml-4 px-2 py-1 text-sm font-semibold">
            <div className="w-2 h-2 rounded-full mt-1" style={{backgroundColor: getAutomationStatusColor(automation?.status)}}></div>
            {startCase(automation?.status.toLowerCase())}
          </span>
        </div>
      </div>
      {/* Main Content */}
      <div className="container mx-auto p-2 md:p-4">
        <div className="grid grid-cols-1 md:grid-cols-8 gap-4 md:p-4">
          {/* Left Column */}
          <div className={`col-span-2 ${hasReviewRequests ? 'md:col-span-3' : 'md:col-span-5'} space-y-4`}>
            {/* Automation Information Card */}
            <div className="bg-white p-2 md:p-4 rounded shadow-sm">
              <h5 className="font-bold text-sm mb-2">Automation Information</h5>
              <hr className="border-gray-200 my-3"/>
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[320px]">
                  <tbody>
                    <tr>
                      <td className="w-[130px] py-2 text-xs text-base-coolGray">
                        AUTOMATION ID
                      </td>
                      <td className="py-2">{automation?.id || '-'}</td>
                    </tr>
                    <tr>
                      <td className="w-[130px] py-2 text-xs text-base-coolGray">
                        AUTOMATION NAME
                      </td>
                      <td className="py-2">{automation?.name || '-'}</td>
                    </tr>
                    <tr>
                      <td className="w-[130px] py-2 text-xs text-base-coolGray">
                        CREATED
                      </td>
                      <td className="py-2">
                        {automation?.createdAt ? new Date(automation.createdAt).toLocaleString() : '-'}
                      </td>
                    </tr>
                  </tbody>
                </table>
                <hr className="border-gray-200 my-3"/>
                <div className="py-2 text-xs text-base-coolGray">
                  AUTOMATION DESCRIPTION
                </div>
                <div>{automation?.description || '-'}</div>
              </div>
            </div>

            {/* Send campaign via Card */}
            <div className="bg-white p-2 md:p-4 rounded shadow-sm">
              <h5 className="font-bold text-sm mb-2">Send via</h5>
              <hr className="border-gray-200 my-3"/>
              <div className="space-y-3">
                {hasEmail && (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded flex items-center justify-center">
                      <EmailIcon />
                    </div>
                    <div className="flex-1">
                      <div className="text-xs text-base-coolGray">
                        EMAIL TEMPLATE
                      </div>
                      <div className="text-xs">Default email template</div>
                    </div>
        
                  </div>
                )}
                {hasSMS && (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded flex items-center justify-center">
                      <IPhoneIcon />
                    </div>
                    <div className="flex-1">
                      <div className="text-xs text-base-coolGray">
                        TEXT TEMPLATE
                      </div>
                      <div className="text-xs">Default text template</div>
                    </div>
                    {/*<button className="bg-[#E7EAF4] rounded-md pl-2 pr-4 py-2 text-sm flex items-center justify-start min-w-[80px]">*/}
                    {/*  <img src={eyeIcon} alt="eye" className="w-4 h-4 mr-2" />*/}
                    {/*  Text preview*/}
                    {/*</button>*/}
                  </div>
                )}
              </div>
            </div>

            {/* Sender Card */}
            <div className="bg-white p-2 md:p-4 rounded shadow-sm">
              <h5 className="font-bold text-sm mb-2">Sender</h5>
              <hr className="border-gray-200 my-3"/>

              <table className="w-full text-sm min-w-[320px]">
                <tbody>
                <tr>
                  <td className="w-[150px] py-2 text-xs text-base-coolGray">
                    BUSINESS NAME
                  </td>
                  <td className="py-2">{automation?.senderName || '-'}</td>
                </tr>
                <tr>
                  <td className="py-2 text-xs text-base-coolGray">
                    BUSINESS ADDRESS
                  </td>
                  <td className="py-2">{automation?.senderAddress || '-'}</td>
                </tr>
                <tr>
                  <td className="py-2 text-xs text-base-coolGray">
                    <div className="w-full flex items-center justify-start gap-2">
                      <span className="bg-base-antiFlash p-1 rounded-sm inline-flex items-center justify-center">
                        <EmailIcon className="w-4 h-4" />
                      </span>
                      <span>BUSINESS EMAIL</span>
                    </div>
                  </td>
                  <td className="py-2">{automation?.senderEmail || '-'}</td>
                </tr>
                <tr>
                  <td className="py-2 text-xs text-base-coolGray">
                    <div className="w-full flex items-center justify-start gap-2">
                      <span className="bg-base-antiFlash p-1 rounded-sm flex items-center justify-center">
                        <IPhoneIcon className="w-4 h-4" />
                      </span>
                      <span>
                        BUSINESS PHONE
                      </span>
                    </div>
                  </td>
                  <td className="py-2">{automation?.senderPhone || '-'}</td>
                </tr>
                </tbody>
              </table>
            </div>

            {/* Automation Card */}
            <div className="bg-white p-2 md:p-4 rounded shadow-sm">
              <h5 className="font-bold text-sm mb-2">Automation</h5>
              <hr className="border-gray-200 my-4"/>
              <div className="flex gap-2 items-center">
                <span className="w-6 h-6 bg-base-antiFlash p-1 rounded-sm inline-flex items-center justify-center">
                  <AutomationIcon className="w-3 h-3 [&_path]:fill-base-coolGray" />
                </span>
                <span className="text-xs uppercase text-base-coolGray">
                  Event
                </span>
              </div>
              <div className="bg-base-seasalt p-2 mt-4">
                <div className="text-xs flex items-center justify-start gap-3 text-base-raisinBlack">
                  <span className="py-1 min-w-[64px] bg-white rounded-sm text-center">
                    <span className="text-xs uppercase text-base-coolGray">If</span>
                  </span>
                  <span className="uppercase">{ getEventText(automation.eventName) }</span>
                </div>
                <hr className="border-gray-200 my-4"/>
                <div className="text-xs uppercase text-base-coolGray my-2">
                  Source
                </div>
                <div className="text-sm my-2 text-base-raisinBlack">
                  {/*{ (condition?.sources || []).map((source: string) => capitalize(source.replace('_', ' '))).join(', ') }*/}
                  { getEventSourceText(automation.eventSource) }
                </div>
              </div>

{/*              {automation.events?.map((event: ConditionInterface) => {*/}
{/*return 'wefwef';*/}
{/*                return (*/}
{/*                    <div key={condition.id} className="bg-base-seasalt p-2 mt-4">*/}
{/*                      <div className="text-xs flex items-center justify-start gap-3 text-base-raisinBlack">*/}
{/*                        <span className="py-1 min-w-[64px] bg-white rounded-sm text-center">*/}
{/*                          <span className="text-xs uppercase text-base-coolGray">If</span>*/}
{/*                        </span>*/}
{/*                        <span className="uppercase">{ getConditionEventText(condition.event) }</span>*/}
{/*                      </div>*/}
{/*                      <hr className="border-gray-200 my-4"/>*/}
{/*                      <div className="text-xs uppercase text-base-coolGray my-2">*/}
{/*                        Source*/}
{/*                      </div>*/}
{/*                      <div className="text-sm my-2 text-base-raisinBlack">*/}
{/*                        { (condition?.sources || []).map((source: string) => capitalize(source.replace('_', ' '))).join(', ') }*/}
{/*                      </div>*/}
{/*                    </div>*/}
{/*                );*/}
{/*              })}*/}

              {/*<div className="flex gap-2 items-center">*/}
              {/*  <span className="w-6 h-6 bg-base-antiFlash p-1 rounded-sm inline-flex items-center justify-center">*/}
              {/*    <AutomationIcon className="w-3 h-3 [&_path]:fill-base-coolGray" />*/}
              {/*  </span>*/}
              {/*  <span className="text-xs uppercase text-base-coolGray">*/}
              {/*    Conditions*/}
              {/*  </span>*/}
              {/*</div>*/}
              {/*{automation.conditions?.map((condition: ConditionInterface) => {*/}

              {/*  return (*/}
              {/*      <div key={condition.id} className="bg-base-seasalt p-2 mt-4">*/}
              {/*        <div className="text-xs flex items-center justify-start gap-3 text-base-raisinBlack">*/}
              {/*          <span className="py-1 min-w-[64px] bg-white rounded-sm text-center">*/}
              {/*            <span className="text-xs uppercase text-base-coolGray">If</span>*/}
              {/*          </span>*/}
              {/*          <span className="uppercase">{ getConditionEventText(condition.event) }</span>*/}
              {/*        </div>*/}
              {/*        <hr className="border-gray-200 my-4"/>*/}
              {/*        <div className="text-xs uppercase text-base-coolGray my-2">*/}
              {/*          Source*/}
              {/*        </div>*/}
              {/*        <div className="text-sm my-2 text-base-raisinBlack">*/}
              {/*          { (condition?.sources || []).map((source: string) => capitalize(source.replace('_', ' '))).join(', ') }*/}
              {/*        </div>*/}
              {/*      </div>*/}
              {/*  );*/}
              {/*})}*/}


              {isReviewEvent && (
                <>
                  <div className="flex gap-2 items-center my-4">
                    <span className="w-6 h-6 bg-base-antiFlash p-1 rounded-sm inline-flex items-center justify-center">
                      <BookIcon className="w-3 h-3 [&_path]:stroke-base-coolGray" />
                    </span>
                    <span className="text-xs uppercase text-base-coolGray">
                      Event conditions
                    </span>
                  </div>

                  <div className="bg-base-seasalt p-3 my-4">
                    <div className="text-xs text-base-coolGray uppercase">
                      Rating
                    </div>

                    <div className="text-sm  my-2 flex flex-wrap gap-2">
                      {automation.eventConditions?.ratings?.map((rating: string, index: number) => {

                        return (
                          <div key={index} className="flex items-center gap-2 bg-base-white rounded-sm px-3 py-2">
                            <FullStarIcon key={index} className="w-4 h-4" />
                            <span className="text-xs text-base-raisinBlack">
                              { rating } { rating == '1' ? 'Star' : 'Stars'}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                </>
              )}

              {hasCustomerFilter && (
                <>
                  <div className="flex gap-2 items-center my-4">
                    <span className="w-6 h-6 bg-base-antiFlash p-1 rounded-sm inline-flex items-center justify-center">
                      <CustomerMultipleIcon className="w-3 h-3 [&_path]:stroke-base-coolGray" />
                    </span>
                    <span className="text-xs uppercase text-base-coolGray">
                      Customer filter
                    </span>
                  </div>

                  <div className="bg-base-seasalt p-3 my-4">
                    <div className="text-xs text-base-coolGray uppercase">
                      Lists
                    </div>

                    <div className="text-sm  my-2 flex flex-wrap gap-2">
                      {automation.lists?.map((list: ListInterface) => {

                        return (
                          <div key={list.id} className="flex items-center gap-2 bg-base-white rounded-sm px-3 py-2">
                            <FolderIcon className="w-4 h-4 [&_path]:stroke-base-coolGray" />
                            <span className="text-xs text-base-raisinBlack">
                              { list.name }
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="bg-base-seasalt p-3 my-4">
                    <div className="text-xs text-base-coolGray uppercase">
                      Tags
                    </div>

                    <div className="text-sm  my-2 flex flex-wrap gap-2">
                      {automation.tags?.map((tag: TagInterface) => {
                        return (
                          <div key={tag.id} className="flex items-center gap-2 bg-base-white rounded-sm px-3 py-2">
                            <span className="text-xs text-base-raisinBlack">
                              { tag.name }
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}

            </div>

            {/* Follow-ups Card */}
            {hasFollowUps && (
                <div className="bg-white p-2 md:p-4 rounded-b shadow-sm">
                  <h5 className="font-bold text-sm mb-2">Follow-ups</h5>
                  <hr className="border-gray-200 my-3"/>
                  <div className="space-y-4">
                    {followUps.length > 0 ? (
                        <>
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 rounded-sm flex items-center justify-center bg-base-antiFlash">
                              <CalendarIcon/>
                            </div>
                            <div className="text-xs text-base-coolGray">
                              SEND AGAIN
                            </div>
                          </div>

                          {followUps?.map((followUp: any) => {

                            return (
                                <div key={followUp.id} className="space-y-2">
                                  <div className="flex items-center gap-2 w-full">
                                    <div className="bg-base-seasalt rounded px-3 py-2 text-sm flex-1">
                                      After {followUp.value} {followUp.period == 'days' ? 'day(s)' : followUp.period}
                                    </div>
                                    <div className="flex gap-1">
                                      <button className="bg-[#E7EAF4] rounded-md pl-2 pr-4 py-2 text-sm flex items-center justify-start min-w-[80px]">
                                        <img src={eyeIcon} alt="eye" className="w-4 h-4 mr-2"/>
                                        Email
                                      </button>
                                      <button className="bg-[#E7EAF4] rounded-md pl-2 pr-4 py-2 text-sm flex items-center justify-start min-w-[80px]">
                                        <img src={eyeIcon} alt="eye" className="w-4 h-4 mr-2"/>
                                        Text
                                      </button>
                                    </div>
                                    <div className="min-w-7">
                                      <button className="w-6 h-6 flex items-center justify-center">
                                        <RemoveIcon />
                                      </button>
                                    </div>
                                  </div>
                                </div>
                            );
                          })}

                        </>
                    ) : (
                        <span className="text-xs text-base-coolGray">
                    No follow-ups
                  </span>
                    )}
                  </div>
                </div>
            )}

          </div>
          {hasReviewRequests && (
            <div className="col-span-1 md:col-span-5">
              <AutomationReviewRequestsTable automationId={automation?.id}/>
              {/*<pre style={{ border: 'solid 1px red', textAlign: 'left' }}>{ JSON.stringify(automationData, null, 4) }</pre>*/}
              {/*<pre style={{ border: 'solid 1px red', textAlign: 'left' }}>{ JSON.stringify(automation, null, 4) }</pre>*/}
            </div>
          )}
          {/* Right Column: Customers */}

        </div>
      </div>
    </div>
  );
};
